import { useMemo, useState } from "react";
import { CheckCircle, Sparkle, Warning, Quotes } from "@phosphor-icons/react";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";

const conf = (c) => (c == null ? "—" : `${Math.round(c * 100)}%`);

/** Review table for autofilled values: extracted values are pre-accepted,
 *  AI-inferred defaults need an explicit accept (human in the loop). */
export default function SpecReview({ fields, gaps, accepted, onToggle, onEdit }) {
  const [tab, setTab] = useState("extracted");
  const entries = useMemo(() => Object.entries(fields || {}), [fields]);
  const found = entries.filter(([, v]) => v.status === "found");
  const inferred = entries.filter(([, v]) => v.status === "inferred");
  const tabs = [
    { key: "extracted", label: "From document", count: found.length, icon: CheckCircle, color: "var(--c-green)" },
    { key: "inferred", label: "AI-inferred", count: inferred.length, icon: Sparkle, color: "var(--c-accent)" },
    { key: "open", label: "Open gaps", count: (gaps || []).length, icon: Warning, color: "var(--c-amber)" },
  ];
  const rows = tab === "extracted" ? found : tab === "inferred" ? inferred : [];

  return (
    <div className="rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)] overflow-hidden" data-testid="spec-review">
      <div className="flex border-b border-[var(--c-border)]">
        {tabs.map((t) => (
          <button key={t.key} data-testid={`spec-review-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs transition-colors duration-150 border-b-2
              ${tab === t.key ? "border-[var(--c-accent)] text-[var(--c-text)]" : "border-transparent text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
            <t.icon size={13} style={{ color: t.color }} /> {t.label}
            <span className="font-mono text-[10px] text-[var(--c-muted)]">{t.count}</span>
          </button>
        ))}
      </div>

      <div className="max-h-96 overflow-y-auto">
        {tab === "open" ? (
          (gaps || []).length === 0 ? (
            <div className="px-4 py-6 text-xs text-[var(--c-muted)]">No open parameters — every field was filled.</div>
          ) : (
            <div className="divide-y divide-[var(--c-border)]">
              {gaps.map((g) => (
                <div key={g.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`spec-gap-${g.id}`}>
                  <span className="text-xs text-[var(--c-text)]">{g.label}</span>
                  <span className={`font-mono text-[10px] ${g.required ? "text-[var(--c-red)]" : "text-[var(--c-muted)]"}`}>
                    {g.required ? "required" : "optional"}
                  </span>
                </div>
              ))}
            </div>
          )
        ) : rows.length === 0 ? (
          <div className="px-4 py-6 text-xs text-[var(--c-muted)]">Nothing in this group.</div>
        ) : (
          <table className="w-full text-xs">
            <tbody className="divide-y divide-[var(--c-border)]">
              {rows.map(([fid, v]) => (
                <tr key={fid} className="hover:bg-[var(--c-bg)]/60 transition-colors duration-150" data-testid={`spec-row-${fid}`}>
                  <td className="px-4 py-2.5 align-top w-[34%]">
                    <div className="text-[var(--c-text)]">{v.label || fid}</div>
                    {v.status === "inferred" ? (
                      <div className="text-[10px] text-[var(--c-muted)] mt-0.5 leading-relaxed">
                        {v.basis && <span className="text-[var(--c-accent)]">{v.basis}</span>}
                        {v.reason ? ` — ${v.reason}` : ""}
                      </div>
                    ) : (v.quote || v.source) && (
                      <div className="text-[10px] text-[var(--c-muted)] mt-0.5 flex gap-1 leading-relaxed">
                        <Quotes size={10} className="mt-0.5 shrink-0" />
                        <span className="line-clamp-2">{v.quote || v.source}{v.page ? ` · p.${v.page}` : ""}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 align-top w-[30%]">
                    <Input data-testid={`spec-value-${fid}`} value={Array.isArray(v.value) ? v.value.join("; ") : String(v.value ?? "")}
                      onChange={(e) => onEdit(fid, e.target.value)}
                      className="h-8 bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono text-xs" />
                  </td>
                  <td className="px-2 py-2.5 align-top font-mono text-[10px] text-[var(--c-muted)] w-[12%]">{v.unit || ""}</td>
                  <td className="px-2 py-2.5 align-top font-mono text-[10px] text-[var(--c-muted)] w-[10%]">{conf(v.confidence)}</td>
                  <td className="px-4 py-2 align-top w-[14%] text-right">
                    <Switch data-testid={`spec-accept-${fid}`} checked={!!accepted[fid]}
                      onCheckedChange={(on) => onToggle(fid, on)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
